import React, { PropsWithChildren, useRef } from 'react';
import * as S from './BottomSheet.styles';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { Portal } from '@/components/UI/Portal/Portal';
import { Overlay } from '@/components/UI/Overlay';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { useAnimatedVisibility } from '@/hooks/useAnimatedVisibility';

interface BottomSheetProps {
  isOpen: boolean;
  close: () => void;
  title?: never;
  isTitleCentered?: never;
  hasHeadBorder?: never;
  isInPortal?: boolean;
  dataTestId?: string;
  hasOverlay?: boolean;
  isPageLocked?: boolean;
  onCloseClick?: () => void;
}

interface BottomSheetWithTitleProps
  extends Omit<
    BottomSheetProps,
    'title' | 'isTitleCentered' | 'hasHeadBorder'
  > {
  title: string;
  hasHeadBorder?: boolean;
  isTitleCentered?: boolean;
}

export const BottomSheet: React.FC<
  PropsWithChildren<BottomSheetProps | BottomSheetWithTitleProps>
> = ({
  children,
  isOpen,
  close,
  title,
  isPageLocked = false,
  isTitleCentered = true,
  hasHeadBorder = true,
  hasOverlay = true,
  isInPortal,
  dataTestId,
  onCloseClick,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isVisible, onAnimationEnd } = useAnimatedVisibility(isOpen);
  useBodyOverflow(isVisible && isPageLocked);

  function renderBottomSheet() {
    return (
      <>
        {hasOverlay && <Overlay isOpen={isOpen} onClick={close} />}
        <S.Box
          ref={ref}
          $isOpen={isOpen}
          data-testid={dataTestId ?? 'bottom-sheet'}
          onAnimationEnd={onAnimationEnd}
        >
          {title && (
            <S.Head
              $isTitleCentered={isTitleCentered}
              $hasBorder={hasHeadBorder}
            >
              <S.Title $variant="3">{title}</S.Title>
              <S.Close appearance="transparent" onClick={onCloseClick ?? close}>
                <CloseIcon />
              </S.Close>
            </S.Head>
          )}
          <S.Body>{children}</S.Body>
        </S.Box>
      </>
    );
  }

  if (!isVisible) return null;

  if (isInPortal) {
    return <Portal>{renderBottomSheet()}</Portal>;
  }

  return renderBottomSheet();
};
